import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { X, Mail, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pages where the popup should never appear (immersive/3D experiences)
const BLOCKED_PATHS = ["/tokyo", "/battles", "/feed"];

export const NewsletterPopup = () => {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const subscribed = localStorage.getItem("newsletter_subscribed");
    if (subscribed) return;
    const dismissed = localStorage.getItem("newsletter_dismissed");
    // Don't show again for 7 days after dismissal
    if (dismissed) {
      const elapsed = Date.now() - parseInt(dismissed);
      if (elapsed < 7 * 24 * 60 * 60 * 1000) return;
    }
    // Don't show twice in the same browser session
    if (sessionStorage.getItem("newsletter_seen")) return;
    // Don't show on immersive/3D pages
    if (BLOCKED_PATHS.includes(location.pathname)) return;

    const timer = setTimeout(() => {
      setShow(true);
      sessionStorage.setItem("newsletter_seen", "true");
    }, 12000);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email, name });
      localStorage.setItem("newsletter_subscribed", "true");
      toast.success("You're in! Welcome to the Gas Station family.");
      setShow(false);
    } catch (error) {
      toast.error("Failed to subscribe. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem("newsletter_dismissed", Date.now().toString());
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="newsletter-popup">
      <div className="relative w-full max-w-md glass border border-neon-cyan/30 p-8 animate-in fade-in zoom-in-95 duration-300">
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
          data-testid="newsletter-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-neon-cyan/10 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-neon-cyan" />
          </div>
          <h3 className="font-heading text-2xl uppercase tracking-tight mb-2">
            Stay Fueled
          </h3>
          <p className="text-zinc-400 text-sm">
            Get exclusive drops, new beats, and special discounts delivered straight to your inbox.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="bg-black/50 border-white/10"
            data-testid="newsletter-name-input"
          />
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email"
            className="bg-black/50 border-white/10"
            required
            data-testid="newsletter-email-input"
          />
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-neon-cyan text-black font-bold uppercase tracking-widest py-5"
            data-testid="newsletter-submit"
          >
            <Mail className="w-4 h-4 mr-2" />
            {submitting ? "Subscribing..." : "Join the Crew"}
          </Button>
        </form>

        <p className="text-center text-xs text-zinc-600 mt-4">
          No spam, ever. Unsubscribe anytime.
        </p>
      </div>
    </div>
  );
};

export default NewsletterPopup;
